// @ts-strict-ignore
// Angular imports required for module local scope
import { JsonPipe } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { CSP_NONCE, inject, NgModule, provideAppInitializer } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
// Shared and standalone imports
import { InputGroupModule } from "primeng/inputgroup";

import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { JsonViewerComponent } from "./shared/components/json-viewer/json-viewer.component";
import { GeneralDialogComponent } from "./shared/components/dialogs/general-dialog/general-dialog.component";
import { HttpErrorDialogComponent } from "./shared/components/dialogs/http-error-dialog/http-error-dialog.component";
import { SessionTimeoutDialogComponent } from "./shared/components/dialogs/session-timeout-dialog/session-timeout-dialog.component";
import { RsIconComponent } from "src/app/shared/components/icons/icon.component";

// Redux core imports and local
import { EffectsModule } from "@ngrx/effects";
import { StoreModule } from "@ngrx/store";
import { AuthEffects } from "./modules/auth/ngrx/auth.effects";
import { ESimEffects } from "./modules/esim/ngrx/esim.effects";
import { reducers } from "./ngrx/root.reducer";

// 3rd party dependencies within module local scope
import { definePreset } from "@primeuix/themes";
import Material from "@primeuix/themes/material";
import { BlockUIModule } from "primeng/blockui";
import { ButtonModule } from "primeng/button";
import { providePrimeNG } from "primeng/config";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { MenubarModule } from "primeng/menubar";
import { ToastModule } from "primeng/toast";
// Microsoft Monaco Editor
import { MonacoEditorModule } from "ngx-monaco-editor-v2";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";

function onMonacoLoad() {
  if (monaco === undefined) {
    return;
  }

  monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
    allowComments: true, // Enable support for JSONC
    validate: true
  });

  // See https://github.com/Microsoft/vscode/blob/main/src/vs/editor/standalone/common/themes.ts
  monaco.editor.defineTheme("ripsim-light", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "string.key.json", foreground: "495057" },
      { token: "string.value.json", foreground: "880000" },
      { token: "number", foreground: "008800" },
      { token: "keyword.json", foreground: "008800" },
      { token: "delimiter", foreground: "495057" }
    ],
    colors: {}
  });
}

// Environment specific imports
import { environmentSpecificModules } from "../environments/app.module.imports";

// Module child imports
import { AppRoutingModule } from "./app-routing.module";

// Service injections
import { MessageService } from "primeng/api";
import { MessageModule } from "primeng/message";
import { APIInterceptorService } from "./api/shared/interceptors/api.interceptor";
import { APIService } from "./api/shared/services/api.service";
import { AuthInterceptorService } from "./modules/auth/shared/interceptors/auth.interceptor";
import { AuthService } from "./modules/auth/shared/services/auth.service";
import { ExpertImportService } from "./modules/esim/shared/services/expert-import.service";
import { ExpertProfileService } from "./modules/esim/shared/services/expert-profile.service";
import { ExpertSchemaService } from "./modules/esim/shared/services/expert-schema.service";
import { ExpertScreensGeneratorService } from "./modules/esim/shared/services/expert-screens-generator.service";
import { ExpertValidatorService } from "./modules/esim/shared/services/expert-validator.service";
import { HttpInterceptorService } from "./shared/interceptors/http.interceptor";
import { TimeoutInterceptorService } from "./shared/interceptors/timeout.interceptor";
import { AppletService } from "./shared/services/applet.service";
import { BootstrapService } from "./shared/services/bootstrap.service";
import { DataService } from "./shared/services/data.service";
import { LogService } from "./shared/services/log.service";
import { ProfileService } from "./shared/services/profile.service";
import { ValidatorService } from "./shared/services/validator.service";
// Components within module declaration scope
import { CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { SelectModule } from "primeng/select";
import { AboutDialogComponent } from "src/app/shared/components/dialogs/about-dialog/about-dialog.component";
import { EsimLookupComponent } from "src/app/shared/components/esim-lookup/esim-lookup.component";
import { ApiModule } from "./api/api.module";
import { AppComponent } from "./components/app/app.component";
import { HeaderComponent } from "./components/header/header.component";
import { PageNotFoundComponent } from "./components/page-not-found/page-not-found.component";
import { CopyrightComponent, CopyrightComponentModule } from "./shared/components/copyright/copyright.component";
import { UtimacoHsmService } from "./shared/services/utimaco-hsm-service.service";

const nonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute("content");

const RsPreset = definePreset(Material, {
  components: {
    toggleswitch: {
      root: {
        width: "2.25rem",
        height: "1.25rem",
        borderRadius: "0.625rem",
        gap: "0.25rem",
        shadow: "none",
        focusRing: {
          width: "1px",
          style: "solid",
          color: "var(--rs-dark-blue)",
          offset: "2px",
          shadow: "none"
        }
      },
      handle: {
        borderRadius: "50%",
        size: "1rem"
      },
      colorScheme: {
        light: {
          root: {
            background: "var(--rs-gray-400)",
            disabledBackground: "var(--rs-gray-300)",
            hoverBackground: "var(--rs-gray-400)",
            checkedBackground: "var(--rs-dark-blue)",
            checkedHoverBackground: "var(--rs-dark-blue)"
          },
          handle: {
            background: "var(--rs-white)",
            disabledBackground: "var(--rs-white)",
            hoverBackground: "var(--rs-white)",
            checkedBackground: "var(--rs-white)",
            checkedHoverBackground: "var(--rs-white)",
            color: "var(--rs-dark-blue)",
            hoverColor: "var(--rs-dark-blue)",
            checkedColor: "var(--rs-dark-blue)",
            checkedHoverColor: "var(--rs-dark-blue)"
          }
        }
      },
      css: `
        .p-toggleswitch {
          border-radius: {toggleswitch.border.radius};
        }
        /* Material's built-in focus glow is on .p-toggleswitch-handle and is
           clipped by the slider's overflow:hidden — paint on the host instead. */
        .p-toggleswitch:has(.p-toggleswitch-input:focus-visible) {
          outline: {toggleswitch.focus.ring.width} {toggleswitch.focus.ring.style} {toggleswitch.focus.ring.color};
          outline-offset: {toggleswitch.focus.ring.offset};
        }
        /* PrimeNG has no checked+disabled token — fill the gap here. */
        .p-toggleswitch.p-disabled.p-toggleswitch-checked .p-toggleswitch-slider {
          background: var(--rs-faded-blue);
        }
      `
    }
  }
});

@NgModule({
  declarations: [AppComponent, PageNotFoundComponent],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    HeaderComponent,
    JsonViewerComponent,
    GeneralDialogComponent,
    HttpErrorDialogComponent,
    SessionTimeoutDialogComponent,
    RsIconComponent,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    AppRoutingModule,
    ButtonModule,
    BlockUIModule,
    DialogModule,
    ToastModule,
    MenubarModule,
    InputTextModule,
    MonacoEditorModule.forRoot({
      baseUrl: "assets",
      onMonacoLoad
    }),
    StoreModule.forRoot(reducers),
    environmentSpecificModules,
    EffectsModule.forRoot([AuthEffects, ESimEffects]),
    CopyrightComponentModule,
    ApiModule.forRoot({ rootUrl: "" }),
    InputGroupModule,
    InputGroupAddonModule,
    IconFieldModule,
    InputIconModule,
    EsimLookupComponent,
    AboutDialogComponent,
    CopyrightComponent
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: APIInterceptorService
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: AuthInterceptorService
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: HttpInterceptorService
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: TimeoutInterceptorService
    },
    { provide: CSP_NONCE, useValue: nonce },
    // Single instance behind the app-wide <p-toast> in AppComponent. It lives in the root injector
    // so root-provided services can publish to it; a component-level provider is out of reach from
    // those services, which the injector reports as a circular dependency (NG0200).
    MessageService,
    AuthService,
    // Resolve auth before the router runs: pick up the locally-stored token or
    // silently sign in against the shared Keycloak session, so a user already
    // signed in (e.g. via the React app at /web) lands authenticated on their
    // deep-linked route instead of bouncing to /auth.
    provideAppInitializer(() => inject(AuthService).bootstrapAuth()),
    APIService,
    LogService,
    DataService,
    ProfileService,
    ValidatorService,
    AppletService,
    BootstrapService,
    ExpertValidatorService,
    ExpertScreensGeneratorService,
    ExpertSchemaService,
    ExpertProfileService,
    ExpertImportService,
    JsonPipe,
    UtimacoHsmService,
    MessageModule,
    SelectModule,
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: RsPreset,
        options: {
          darkModeSelector: false || "none",
          cssLayer: {
            name: "primeng"
          }
        }
      },
      csp: {
        nonce: nonce ?? undefined
      }
    })
  ]
})
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class AppModule {}
